import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2,
    Mail,
    UserPlus,
    UserCheck,
    Trash2,
    Copy,
    ArrowLeft,
    AlertCircle,
    DollarSign,
    History,
    Search,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    FileCheck2,
    FileWarning,
    FileX2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PERMISSION_LABELS, PERMISSION_GROUPS, PERMISSION_DEFAULTS, summarizeGrants } from "@/lib/permissions";
import CoachRosterDrawer, { PermissionToggle } from "@/components/coach/CoachRosterDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PERMS = { ...PERMISSION_DEFAULTS };

export default function CoachManagement() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [coaches, setCoaches] = useState([]);
    const [invites, setInvites] = useState([]);
    const [signaturesByUser, setSignaturesByUser] = useState({});
    const [busy, setBusy] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("name_asc");
    const [activeCoachId, setActiveCoachId] = useState(null);
    const [fixingStatus, setFixingStatus] = useState(false);

    const activeCoach = coaches.find((c) => c.user_id === activeCoachId);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "owner") {
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const refresh = async () => {
        setBusy(true);
        try {
            const [c, i, s] = await Promise.all([
                axios.get(`${API}/coach/coaches`, { withCredentials: true }),
                axios.get(`${API}/coach/invites`, { withCredentials: true }),
                axios.get(`${API}/coach/contracts/signatures`, { withCredentials: true }),
            ]);
            setCoaches(c.data || []);
            setInvites(i.data || []);
            // Index signatures by user_id for O(1) row lookup
            const map = {};
            for (const row of (s.data?.signatures || [])) map[row.user_id] = row;
            setSignaturesByUser(map);
        } catch (e) {
            toast.error("Couldn't load coaches", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (user?.role === "owner") refresh();
    }, [user]);

    // Layer 2: promote an existing user (someone who signed in normally
    // instead of clicking the invite accept link). Solves the "Brian"
    // problem — invited coach who landed on the parent dashboard.
    const promote = async (email) => {
        const e = (email || "").trim().toLowerCase();
        if (!e) return;
        try {
            const r = await axios.post(
                `${API}/coach/promote`,
                { email: e },
                { withCredentials: true },
            );
            const dupes = r.data?.removed_duplicates || 0;
            toast.success("Promoted to coach", {
                description: dupes > 0
                    ? `${e} is now a coach. ${dupes} duplicate account${dupes === 1 ? "" : "s"} removed — they'll see the coach dashboard on their next sign-in.`
                    : `${e} is now a coach — they'll see the coach dashboard on their next sign-in.`,
            });
            await refresh();
            return r.data;
        } catch (err) {
            toast.error("Couldn't promote", { description: err?.response?.data?.detail });
            return null;
        }
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    // Staff whose status is still a client status (e.g. a coach promoted from
    // a lead kept "lead") — offer a one-click fix so reports stop counting them.
    const staleStatusCount = coaches.filter(
        (c) => c.status && c.status !== c.role,
    ).length;

    const normalizeStatuses = async () => {
        setFixingStatus(true);
        try {
            const r = await axios.post(`${API}/coach/coaches/normalize-status`, {}, { withCredentials: true });
            const n = r.data?.fixed ?? 0;
            toast.success(n > 0 ? `Fixed ${n} coach status${n === 1 ? "" : "es"}` : "All coach statuses already clean");
            if (n > 0) refresh();
        } catch (e) {
            toast.error("Couldn't fix statuses", { description: e?.response?.data?.detail });
        } finally {
            setFixingStatus(false);
        }
    };

    return (
        <div data-testid="coach-management" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-10 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Link to="/coach" data-testid="coach-mgmt-back" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Back to roster
                    </Link>
                    <div className="grid md:grid-cols-12 gap-6 items-end">
                        <div className="md:col-span-8">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" />
                                Owner controls
                            </div>
                            <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                                MANAGE <span className="text-brand-red">COACHES.</span>
                            </h1>
                            <p className="mt-4 text-zinc-200 max-w-2xl">
                                Invite coaches and pick exactly what each can see. You can change permissions or revoke
                                access any time.
                            </p>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <button
                                type="button"
                                data-testid="coach-mgmt-invite-open"
                                onClick={() => setInviteOpen(true)}
                                className="btn-tactical inline-flex"
                            >
                                <UserPlus className="w-4 h-4" /> Invite coach
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {inviteOpen && <InvitePanel onClose={() => setInviteOpen(false)} onCreated={refresh} />}

            {/* Layer 2 — Quick promote existing user */}
            <PromoteRow promote={promote} />

            {staleStatusCount > 0 && (
                <div className="max-w-7xl mx-auto px-6 md:px-10 -mt-2 mb-2">
                    <div data-testid="coach-status-cleanup" className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between border border-amber-500/30 bg-amber-500/10 rounded-md px-4 py-3">
                        <div className="text-sm text-amber-100">
                            <span className="font-semibold">{staleStatusCount}</span> coach{staleStatusCount === 1 ? "" : "es"} still carry a client status (e.g. &ldquo;lead&rdquo;), which can miscount them in reports.
                        </div>
                        <button
                            type="button"
                            data-testid="coach-status-cleanup-fix"
                            onClick={normalizeStatuses}
                            disabled={fixingStatus}
                            className="inline-flex items-center justify-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/40 text-xs tracking-[0.15em] uppercase px-4 py-2.5 rounded transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            {fixingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Fix coach statuses
                        </button>
                    </div>
                </div>
            )}

            {/* Coaches list — compact table */}
            <CoachRosterTable
                coaches={coaches}
                signaturesByUser={signaturesByUser}
                busy={busy}
                query={query}
                onQuery={setQuery}
                sort={sort}
                onSort={setSort}
                onOpen={(c) => setActiveCoachId(c.user_id)}
            />

            {/* Pending invites */}
            <section className="pb-16">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-4">Recent invitations</div>
                    {invites.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950 p-6 text-sm text-zinc-300">
                            No invitations sent yet.
                        </div>
                    ) : (
                        <div className="border border-zinc-900">
                            {invites.map((inv) => (
                                <div
                                    key={inv.invite_id}
                                    className="px-5 py-4 border-b border-zinc-900 last:border-b-0 bg-zinc-950 flex items-center justify-between gap-3"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm text-white truncate">{inv.email}</div>
                                        <div className="text-xs text-zinc-300 truncate">
                                            {inv.used ? `Accepted ${inv.used_at ? new Date(inv.used_at).toLocaleDateString() : ""}` : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                                        </div>
                                    </div>
                                    <span className={`inline-flex text-[10px] tracking-[0.25em] uppercase border px-2.5 py-1 ${
                                        inv.used
                                            ? "border-emerald-500/50 text-emerald-400"
                                            : inv.email_sent === false
                                                ? "border-amber-500/60 text-amber-300"
                                                : "border-zinc-700 text-zinc-200"
                                    }`}>
                                        {inv.used
                                            ? "Accepted"
                                            : inv.email_sent === false
                                                ? "Email failed — copy URL"
                                                : "Pending"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {activeCoach && (
                <CoachRosterDrawer
                    coach={activeCoach}
                    allCoaches={coaches}
                    onClose={(forceRefresh) => {
                        setActiveCoachId(null);
                        if (forceRefresh) refresh();
                    }}
                    onChanged={(next) => {
                        setCoaches((arr) => arr.map((c) => c.user_id === next.user_id ? next : c));
                    }}
                />
            )}
        </div>
    );
}

// ---------- Compact roster table ----------
// One row per coach. Search + sort. Click any row → opens the drawer.
function CoachRosterTable({ coaches, signaturesByUser, busy, query, onQuery, sort, onSort, onOpen }) {
    const filtered = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        let rows = !q
            ? coaches
            : coaches.filter((c) => {
                const hay = `${c.name || ""} ${c.email || ""}`.toLowerCase();
                return hay.includes(q);
            });
        const cmp = {
            name_asc: (a, b) => (a.name || a.email).localeCompare(b.name || b.email),
            name_desc: (a, b) => (b.name || b.email).localeCompare(a.name || a.email),
            last_login_desc: (a, b) => (b.last_login_at || "").localeCompare(a.last_login_at || ""),
            last_login_asc: (a, b) => (a.last_login_at || "").localeCompare(b.last_login_at || ""),
            pay_desc: (a, b) => (b.pay?.rate_cents ?? -1) - (a.pay?.rate_cents ?? -1),
            pay_asc: (a, b) => (a.pay?.rate_cents ?? Infinity) - (b.pay?.rate_cents ?? Infinity),
            role_first: (a, b) => {
                if (a.role === b.role) return (a.name || a.email).localeCompare(b.name || b.email);
                return a.role === "owner" ? -1 : 1;
            },
        }[sort] || ((a, b) => 0);
        return [...rows].sort(cmp);
    }, [coaches, query, sort]);

    return (
        <section className="py-8">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">
                        Active coaches · <span className="text-white">{filtered.length}</span> of {coaches.length}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => onQuery(e.target.value)}
                                placeholder="Search name or email"
                                data-testid="coach-roster-search"
                                className="bg-black border border-zinc-800 pl-8 pr-3 py-2 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red w-[220px]"
                            />
                        </div>
                        <select
                            value={sort}
                            onChange={(e) => onSort(e.target.value)}
                            data-testid="coach-roster-sort"
                            className="bg-black border border-zinc-800 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-brand-red"
                        >
                            <option value="name_asc">Name (A–Z)</option>
                            <option value="name_desc">Name (Z–A)</option>
                            <option value="last_login_desc">Last login (recent)</option>
                            <option value="last_login_asc">Last login (oldest)</option>
                            <option value="pay_desc">Pay (high → low)</option>
                            <option value="pay_asc">Pay (low → high)</option>
                            <option value="role_first">Owners first</option>
                        </select>
                    </div>
                </div>

                {busy ? (
                    <div className="py-12 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="border border-zinc-900 bg-zinc-950 p-8 text-center text-sm text-zinc-300">
                        {coaches.length === 0
                            ? "No coaches yet. Send your first invite above."
                            : "No coaches match that search."}
                    </div>
                ) : (
                    <div data-testid="coach-roster-table" className="border border-zinc-900 bg-zinc-950">
                        {filtered.map((c) => (
                            <button
                                key={c.user_id}
                                type="button"
                                data-testid={`coach-row-${c.user_id}`}
                                onClick={() => onOpen(c)}
                                className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-zinc-900 last:border-b-0 hover:bg-black/50 transition-colors group"
                            >
                                <span className="inline-flex items-center justify-center w-9 h-9 bg-brand-red text-white font-display text-sm flex-shrink-0 rounded-full">
                                    {(c.name?.[0] || c.email[0] || "C").toUpperCase()}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-base font-semibold text-white truncate">
                                            {c.name || c.email}
                                        </span>
                                        {c.role === "owner" && (
                                            <span className="inline-flex text-[9px] tracking-[0.2em] uppercase border border-brand-red text-brand-red px-1.5 py-0.5">
                                                Owner
                                            </span>
                                        )}
                                        <ContractStatusBadge sig={signaturesByUser?.[c.user_id]} />
                                        {c.status && c.status !== c.role && (
                                            <span
                                                data-testid={`coach-stale-status-${c.user_id}`}
                                                title="This coach carries a client status — use 'Fix coach statuses' so reports don't count them"
                                                className="inline-flex text-[9px] tracking-[0.2em] uppercase border border-amber-500/40 text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded"
                                            >
                                                status: {c.status}
                                            </span>
                                        )}
                                    </div>
                                    <PermissionSummary permissions={c.permissions} role={c.role} />
                                </div>
                                <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-brand-red transition-colors hidden sm:inline-flex items-center gap-1">
                                    Manage
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </span>
                                <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-brand-red transition-colors sm:hidden" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function PermissionSummary({ permissions, role }) {
    const s = summarizeGrants(permissions, role);
    const chip = "text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded border";
    if (s.full) {
        return (
            <div className="mt-1.5 flex flex-wrap gap-1" data-testid="coach-grants-summary">
                <span className={`${chip} bg-emerald-500/15 text-emerald-300 border-emerald-500/30`}>Full access</span>
            </div>
        );
    }
    const nothing = s.groups.length === 0 && s.owner.length === 0;
    return (
        <div className="mt-1.5 flex flex-wrap gap-1" data-testid="coach-grants-summary">
            {nothing && <span className="text-[10px] text-zinc-500">No access granted yet</span>}
            {s.groups.map((g) => (
                <span key={g.key} className={`${chip} bg-zinc-800 text-zinc-300 border-zinc-700`}>
                    {g.label}{g.partial ? ` ${g.granted}/${g.total}` : ""}
                </span>
            ))}
            {s.owner.map((label) => (
                <span key={label} className={`${chip} bg-amber-500/15 text-amber-300 border-amber-500/30`}>
                    {label}
                </span>
            ))}
        </div>
    );
}

function ContractStatusBadge({ sig }) {
    // sig: per-user row from /coach/contracts/signatures
    // { is_current, signed_version, signed_at, ... }
    if (!sig) return null;
    if (sig.is_current) {
        return (
            <span
                data-testid={`contract-badge-${sig.user_id}`}
                title={`Signed v${sig.signed_version} · ${new Date(sig.signed_at).toLocaleDateString()}`}
                className="inline-flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-emerald-300 border border-emerald-700/50 px-1.5 py-0.5"
            >
                <FileCheck2 className="w-3 h-3" /> Contract
            </span>
        );
    }
    if (sig.signed_version) {
        return (
            <span
                data-testid={`contract-badge-${sig.user_id}`}
                title={`Signed v${sig.signed_version} — current is newer`}
                className="inline-flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-amber-300 border border-amber-700/50 px-1.5 py-0.5"
            >
                <FileWarning className="w-3 h-3" /> Re-sign
            </span>
        );
    }
    return (
        <span
            data-testid={`contract-badge-${sig.user_id}`}
            title="Has not signed the contract"
            className="inline-flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-rose-300 border border-rose-700/50 px-1.5 py-0.5"
        >
            <FileX2 className="w-3 h-3" /> Unsigned
        </span>
    );
}



function PromoteRow({ promote }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        const ok = await promote(email);
        setBusy(false);
        if (ok) setEmail("");
    };
    return (
        <section className="py-6 border-b border-zinc-900 bg-zinc-950/40">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <form
                    onSubmit={submit}
                    data-testid="coach-promote-form"
                    className="flex flex-col md:flex-row md:items-center gap-3"
                >
                    <div className="flex-1">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 mb-1.5">
                            Promote existing user → coach
                        </div>
                        <p className="text-xs text-zinc-300">
                            Use when a coach signed in normally instead of clicking the invite link. They become a
                            coach instantly with default permissions.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="coach@example.com"
                            data-testid="coach-promote-email"
                            className="bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500 min-w-[240px]"
                        />
                        <button
                            type="submit"
                            disabled={busy || !email.trim()}
                            data-testid="coach-promote-submit"
                            className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50 text-xs"
                        >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                            Promote
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

function InvitePanel({ onClose, onCreated }) {
    const [email, setEmail] = useState("");
    const [perms, setPerms] = useState(DEFAULT_PERMS);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [warning, setWarning] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        setWarning(null);
        try {
            const r = await axios.post(
                `${API}/coach/invite`,
                {
                    email,
                    permissions: perms,
                    accept_url_base: `${window.location.origin}/coach/accept`,
                },
                { withCredentials: true },
            );
            setResult(r.data);
            if (r.data.email_sent === false || r.data.email_delivery_warning) {
                setWarning(r.data.email_delivery_warning || "Email could not be delivered. Copy the link below and send it manually.");
                toast.warning("Invite saved, but email didn't send", {
                    description: "Copy the link below and send it to the coach manually.",
                });
            } else {
                toast.success("Invite sent", { description: `Email is on its way to ${email}.` });
            }
            onCreated && onCreated();
        } catch (err) {
            toast.error("Couldn't send invite", { description: err?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    const copyLink = async () => {
        if (!result?.accept_url_for_owner) return;
        await navigator.clipboard.writeText(result.accept_url_for_owner);
        toast.success("Link copied");
    };

    return (
        <section
            data-testid="coach-invite-panel"
            className="bg-black border-b border-zinc-900 py-10"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="border border-brand-red/40 bg-brand-red/5 p-6 md:p-8">
                    {!result ? (
                        <form onSubmit={submit} className="grid md:grid-cols-12 gap-6">
                            <div className="md:col-span-5">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-2">Invite a coach</div>
                                <div className="font-display text-2xl tracking-wider text-white mb-4">
                                    THEY GET AN EMAIL.<br /> ONE CLICK ACCEPTS.
                                </div>
                                <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">
                                    Email <span className="text-brand-red">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Mail className="w-4 h-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input
                                            type="email"
                                            data-testid="coach-invite-email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="coach@example.com"
                                            className="w-full bg-black border border-zinc-800 pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6 flex gap-2">
                                    <button
                                        type="submit"
                                        data-testid="coach-invite-send"
                                        disabled={sending || !email}
                                        className="btn-tactical disabled:opacity-50"
                                    >
                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                        Send invite
                                    </button>
                                    <button type="button" onClick={onClose} className="btn-ghost-tactical">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                            <div className="md:col-span-7">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">
                                    Permissions for this coach
                                </div>
                                <div className="space-y-4">
                                    {PERMISSION_GROUPS.map((group) => (
                                        <div key={group.key}>
                                            <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1.5">{group.label}</div>
                                            <div className="grid sm:grid-cols-2 gap-2.5">
                                                {group.keys.map((key) => (
                                                    <PermissionToggle
                                                        key={key}
                                                        permKey={key}
                                                        enabled={!!perms[key]}
                                                        onChange={(v) => setPerms({ ...perms, [key]: v })}
                                                        testId={`coach-invite-perm-${key}`}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </form>
                    ) : (
                        <div data-testid="coach-invite-result">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-2">
                                Invite created
                            </div>
                            <div className="font-display text-2xl tracking-wider text-white mb-4">
                                INVITED {result.email.toUpperCase()}
                            </div>
                            {warning && (
                                <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 p-3 mb-4 text-xs text-amber-100">
                                    <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-amber-200 mb-1">Email didn't deliver — invite is still valid</div>
                                        <div className="text-zinc-200">{warning}</div>
                                        <div className="text-zinc-300 mt-1">Copy the accept URL below and send it to the coach manually (text, Slack, your own email — anything works).</div>
                                    </div>
                                </div>
                            )}
                            <div className="border border-zinc-900 bg-black p-3 flex items-center gap-3">
                                <code className="text-xs text-zinc-300 truncate flex-1">{result.accept_url_for_owner}</code>
                                <button
                                    type="button"
                                    onClick={copyLink}
                                    data-testid="coach-invite-copy"
                                    className="btn-ghost-tactical inline-flex"
                                >
                                    <Copy className="w-4 h-4" /> Copy
                                </button>
                            </div>
                            <div className="mt-5 flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setResult(null); setEmail(""); }}
                                    className="btn-ghost-tactical"
                                >
                                    Send another
                                </button>
                                <button type="button" onClick={onClose} className="btn-tactical">Done</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
